import { useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModal, useBasic } from 'contexts';
import { BadgeInfo, RewardsConfig } from 'types';
import { HiOutlineTicket } from 'react-icons/hi';
import { BsCoin } from 'react-icons/bs';
import { GrCertificate } from 'react-icons/gr';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

import rewardSound70 from '../../assets/sounds/seventy-sound.mp3';
import rewardSound100 from '../../assets/sounds/hundred-sound.mp3';

import './style.scss';

const SILVER_CONFETTI_COLORS = ['#C0C0C0', '#E8E8E8', '#A9A9A9', '#D3D3D3', '#B0B0B0', '#808080'];
const GOLD_CONFETTI_COLORS = ['#FFD700', '#FFA500', '#FFEC8B', '#DAA520', '#F0E68C', '#FF8C00', '#FFB700'];

type CheckpointRewardModalProps = {
  checkpoint: 70 | 100;
  badge: BadgeInfo | null;
  rewardsConfig: RewardsConfig | null;
  courseID: string;
  ticketType: 'silver' | 'gold';
};

export function CheckpointRewardModal({
  checkpoint,
  badge,
  rewardsConfig,
  courseID,
  ticketType,
}: CheckpointRewardModalProps) {
  const { closeModal } = useModal();
  const { pointsCategories } = useBasic();
  const navigate = useNavigate();
  const { width, height } = useWindowSize();

  const is100 = checkpoint === 100;

  const playSound = useCallback(() => {
    try {
      const audio = new Audio(is100 ? rewardSound100 : rewardSound70);
      audio.volume = 0.5;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => console.log('[Sound] Tocando!'))
          .catch((err) => console.warn('[Sound] Bloqueado pelo navegador:', err));
      }
    } catch (err) {
      console.error('[Sound] Erro ao criar Audio:', err);
    }
  }, [is100]);

  useEffect(() => {
    playSound();
  }, [playSound]);

  const coinAmount = useMemo(() => {
    if (!rewardsConfig?.coins?.enabled) return 0;
    return checkpoint === 70 ? rewardsConfig.coins.amount70 : rewardsConfig.coins.amount100;
  }, [rewardsConfig, checkpoint]);

  const coinsCategory = useMemo(() => {
    return pointsCategories?.find(p => p._id === 'coins');
  }, [pointsCategories]);

  const ticketsEnabled = rewardsConfig?.tickets?.enabled ?? false;

  return (
    <>
      <Confetti
        width={width}
        height={height}
        recycle={true}
        numberOfPieces={is100 ? 600 : 150}
        gravity={is100 ? 0.2 : 0.1}
        colors={is100 ? GOLD_CONFETTI_COLORS : SILVER_CONFETTI_COLORS}
        opacity={is100 ? 0.9 : 0.7}
        wind={is100 ? 0.01 : 0.005}
        style={{ zIndex: 100000, position: 'fixed', top: 0, left: 0 }}
      />
      <div className={`checkpoint-reward-modal modal-appear-${ticketType}`}>
        <div className="decorative-dots">
          <span className="dot dot-1" />
          <span className="dot dot-2" />
          <span className="dot dot-3" />
          <span className="dot dot-4" />
        </div>

        <div className="rewards-display">
          {ticketsEnabled && (
            <div className="reward-item ticket-reward">
              <div className={`ticket-visual ${ticketType}`}>
                <HiOutlineTicket className="ticket-icon" />
                <div className="ticket-label">
                  <span className="ticket-brand">PLAYDPS</span>
                  <span className="ticket-type">
                    {ticketType === 'gold' ? 'GOLDEN' : 'SILVER'}
                  </span>
                  <span className="ticket-type-sub">TICKET</span>
                </div>
              </div>
              <span className="reward-name">
                Bilhete de {ticketType === 'gold' ? 'Ouro' : 'Prata'}
              </span>
            </div>
          )}

          {ticketsEnabled && badge && <span className="plus-sign">+</span>}

          {badge && (
            <div className={`reward-item badge-reward ${!ticketsEnabled ? 'badge-only' : ''}`}>
              <div className={`badge-visual ${is100 ? 'gold' : 'silver'}`}>
                {badge.badge?.small?.url ? (
                  <img src={badge.badge.small.url} alt={badge.challenge} className="badge-img" />
                ) : badge.image?.small?.url ? (
                  <img src={badge.image.small.url} alt={badge.challenge ?? badge.title} className="badge-img" />
                ) : (
                  <div className="badge-placeholder">
                    <span className="badge-star">★</span>
                  </div>
                )}
              </div>
              <span className="reward-name">
                {badge.challenge ?? badge.title ?? (is100 ? 'Gold Badge' : 'Novo Badge')}
              </span>
            </div>
          )}
        </div>

        <h2 className="modal-title">Incrível!</h2>

        <p className="modal-subtitle">
          {ticketsEnabled ? (
            is100 ? (
              <>Você conquistou o Bilhete de Ouro + um novo Badge!</>
            ) : (
              <>Parabéns! Você desbloqueou o Bilhete de Prata + um novo Badge!</>
            )
          ) : (
            <>Parabéns! Você desbloqueou um novo Badge!</>
          )}
        </p>

        <p className="modal-description">
          {is100 ? (
            <>
              Parabéns por completar 100% da trilha! Sua dedicação valeu a pena e agora
              você é oficialmente um especialista. Aproveite suas recompensas exclusivas!
            </>
          ) : (
            <>
              Você atingiu o marco de <strong>70%</strong> de conclusão do curso.
              Suas novas recompensas já estão disponíveis em seu perfil.
            </>
          )}
        </p>

        {coinAmount > 0 && (
          <div className="coins-banner">
            {coinsCategory?.image ? (
              <img
                src={coinsCategory.image}
                alt={coinsCategory.category}
                className="coin-icon"
                style={{ width: 18, height: 18, objectFit: 'contain' }}
              />
            ) : (
              <BsCoin className="coin-icon" />
            )}
            <span>+{coinAmount} {coinsCategory?.category ?? 'Moedas'}</span>
          </div>
        )}

        <div className="modal-actions">
          {is100 ? (
            <>
              <button
                className="btn-primary"
                onClick={() => {
                  closeModal();
                  navigate(`/cursos/${courseID}`);
                }}
              >
                <GrCertificate className="mr-2" />
                Ver Certificado
              </button>
              <button className="btn-secondary" onClick={() => closeModal()}>
                Fechar
              </button>
            </>
          ) : (
            <button
              className="btn-primary full"
              onClick={() => closeModal()}
            >
              Continuar Trilha →
            </button>
          )}
        </div>
      </div>
    </>
  );
}